import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import {
  approveClaim,
  getClaim,
  previewClaimDecision,
  rejectClaim,
  requestClaimInformation,
} from "../../api/clientVendorDirectoryClaimApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.claims";

const REJECTION_REASONS = [
  "unable_to_verify_relationship",
  "contact_verification_failed",
  "evidence_insufficient",
  "evidence_mismatch",
  "existing_authorized_owner",
  "claim_conflict",
  "vendor_record_ineligible",
  "claimant_not_authorized",
  "suspected_fraud",
  "duplicate_claim",
  "other",
];

function statusBadgeClass(status) {
  if (status === "approved") return "cvd-badge--published";
  if (["rejected", "revoked", "expired"].includes(status)) return "cvd-badge--suspended";
  if (["pending_review", "awaiting_information", "disputed"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function riskBadgeClass(risk) {
  if (risk === "high") return "cvd-badge--suspended";
  if (risk === "medium") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

export default function ClientVendorDirectoryClaimDetailPage() {
  const { clientViewId, claimId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const basePath = `${serviciosBase}/reclamaciones`;
  const viewName = activeView?.name || "CasaMapa";

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerTone, setBannerTone] = useState("success");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectionReason, setRejectionReason] = useState(REJECTION_REASONS[0]);
  const [infoMessage, setInfoMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getClaim(clientViewId, claimId);
      setDetail(body);
      setPreview(null);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.detail.loadError`));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [claimId, clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const showBanner = (message, tone = "success") => {
    setBannerMessage(message);
    setBannerTone(tone);
    window.setTimeout(() => setBannerMessage(""), 5000);
  };

  const runAction = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      showBanner(successMessage);
      await load();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.detail.actionError`), "warning");
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = () =>
    runAction(async () => {
      const body = await previewClaimDecision(clientViewId, claimId, {
        previewVersion: detail?.claim?.previewVersion,
      });
      setPreview(body.preview);
    }, t(`${COPY_PREFIX}.detail.previewSuccess`));

  const handleApprove = () =>
    runAction(async () => {
      await approveClaim(clientViewId, claimId, {
        previewVersion: detail?.claim?.previewVersion,
        approvalReason: "administrator_review",
      });
    }, t(`${COPY_PREFIX}.detail.approveSuccess`));

  const handleReject = () =>
    runAction(async () => {
      await rejectClaim(clientViewId, claimId, {
        previewVersion: detail?.claim?.previewVersion,
        rejectionReason,
      });
    }, t(`${COPY_PREFIX}.detail.rejectSuccess`));

  const handleRequestInfo = () =>
    runAction(async () => {
      await requestClaimInformation(clientViewId, claimId, {
        message: infoMessage,
        fields: ["relationship_clarification"],
      });
      setInfoMessage("");
    }, t(`${COPY_PREFIX}.detail.requestInfoSuccess`));

  if (loading) {
    return (
      <div className="page-content cvd-admin cvd-claims">
        <ClientPortalLoading message={t(`${COPY_PREFIX}.detail.loading`)} />
      </div>
    );
  }

  if (errorMessage && !detail?.claim) {
    return (
      <div className="page-content cvd-admin cvd-claims">
        <ClientPortalError message={errorMessage} onRetry={load} />
      </div>
    );
  }

  const claim = detail?.claim;
  const riskEvaluation = detail?.riskEvaluation || claim?.riskEvaluation;

  return (
    <div className="page-content cvd-admin cvd-claims">
      <div className="cvd-admin__header">
        <div>
          <p className="cvd-run-detail__eyebrow">{claim?.publicReference || claimId}</p>
          <h1 className="cvd-admin__title">{claim?.vendorDisplayName || t(`${COPY_PREFIX}.detail.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
          <div className="cvd-run-detail__meta">
            <span className={`cvd-badge ${statusBadgeClass(claim?.status)}`}>
              {t(`${COPY_PREFIX}.status.${claim?.status}`, {}, claim?.status)}
            </span>
            {claim?.riskLevel ? (
              <span className={`cvd-badge ${riskBadgeClass(claim.riskLevel)}`}>
                {t(`${COPY_PREFIX}.risk.${claim.riskLevel}`, {}, claim.riskLevel)}
              </span>
            ) : null}
          </div>
        </div>
        <div className="cvd-admin__actions">
          <Link className="cvd-btn cvd-btn--ghost" to={basePath}>
            ← {t(`${COPY_PREFIX}.backToList`)}
          </Link>
        </div>
      </div>

      {bannerMessage ? (
        <div className={`cvd-alert cvd-alert--${bannerTone === "warning" ? "warning" : "success"}`}>{bannerMessage}</div>
      ) : null}

      <div className="cvd-run-detail__grid">
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.summary`)}</h2>
          <dl className="cvd-dl">
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.reference`)}</dt>
              <dd>{claim?.publicReference || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.table.claimant`)}</dt>
              <dd>{claim?.claimantAccountEmail || claim?.claimantEmail || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.relationship`)}</dt>
              <dd>
                {t(`${COPY_PREFIX}.relationship.${claim?.relationshipToVendor}`, {}, claim?.relationshipToVendor)}
              </dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.verificationMethod`)}</dt>
              <dd>
                {claim?.verificationMethod
                  ? t(`${COPY_PREFIX}.verificationMethod.${claim.verificationMethod}`, {}, claim.verificationMethod)
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.contactVerified`)}</dt>
              <dd>
                {claim?.contactVerifiedAt
                  ? formatDateTime(claim.contactVerifiedAt, locale)
                  : t(`${COPY_PREFIX}.detail.no`)}
              </dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.table.evidence`)}</dt>
              <dd>{claim?.evidenceCount ?? 0}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.submitted`)}</dt>
              <dd>{formatDateTime(claim?.submittedAt || claim?.createdAt, locale)}</dd>
            </div>
          </dl>
        </section>

        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.riskEvaluation`)}</h2>
          {riskEvaluation ? (
            <>
              <p className="muted">
                <strong>{t(`${COPY_PREFIX}.detail.positiveSignals`)}:</strong>{" "}
                {(riskEvaluation.positiveSignals || []).join(", ") || "—"}
              </p>
              <p className="muted">
                <strong>{t(`${COPY_PREFIX}.detail.riskSignals`)}:</strong>{" "}
                {(riskEvaluation.riskSignals || []).join(", ") || "—"}
              </p>
              <p className="muted">
                {t(`${COPY_PREFIX}.detail.autoApproval`)}:{" "}
                {riskEvaluation.automaticApprovalEligible
                  ? t(`${COPY_PREFIX}.detail.yes`)
                  : t(`${COPY_PREFIX}.detail.no`)}
              </p>
            </>
          ) : (
            <p className="muted">{t(`${COPY_PREFIX}.detail.noRiskEvaluation`)}</p>
          )}
        </section>
      </div>

      <section className="cvd-panel cvd-claim-decisions">
        <h2>{t(`${COPY_PREFIX}.detail.decisions`)}</h2>

        <div className="cvd-claim-decisions__primary">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CLAIMS_APPROVE}>
            <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={handlePreview}>
              {t(`${COPY_PREFIX}.detail.preview`)}
            </button>
            <button type="button" className="cvd-btn cvd-btn--primary" disabled={busy} onClick={handleApprove}>
              {t(`${COPY_PREFIX}.detail.approve`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>

        {preview ? (
          <div className="cvd-claim-preview muted">
            {t(`${COPY_PREFIX}.detail.previewRole`)}: <strong>{preview.accessRoleToGrant}</strong>.{" "}
            {t(`${COPY_PREFIX}.detail.previewNote`)}
          </div>
        ) : null}

        <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CLAIMS_REQUEST_INFORMATION}>
          <div className="cvd-claim-decisions__form">
            <label className="cvd-field" htmlFor="info-message">
              {t(`${COPY_PREFIX}.detail.requestInfo`)}
              <textarea
                id="info-message"
                rows={3}
                value={infoMessage}
                onChange={(event) => setInfoMessage(event.target.value)}
                placeholder={t(`${COPY_PREFIX}.detail.requestInfoPlaceholder`)}
                disabled={busy}
              />
            </label>
            <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={handleRequestInfo}>
              {t(`${COPY_PREFIX}.detail.requestInfoAction`)}
            </button>
          </div>
        </VendorDirectoryPermissionGate>

        <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CLAIMS_REJECT}>
          <div className="cvd-claim-decisions__form">
            <label className="cvd-field" htmlFor="reject-reason">
              {t(`${COPY_PREFIX}.detail.rejectReason`)}
              <select
                id="reject-reason"
                value={rejectionReason}
                onChange={(event) => setRejectionReason(event.target.value)}
                disabled={busy}
              >
                {REJECTION_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {t(`${COPY_PREFIX}.rejectionReason.${reason}`)}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="cvd-btn cvd-btn--ghost" disabled={busy} onClick={handleReject}>
              {t(`${COPY_PREFIX}.detail.reject`)}
            </button>
          </div>
        </VendorDirectoryPermissionGate>
      </section>
    </div>
  );
}
